import { useMemo } from 'react';
import { SectionProps } from './props';
import styles from './styles.module.scss';
import { ErrorCard } from '../ErrorCard';
import { PaginationWithProgress } from '../PaginationWithProgress';
import { NoDataComponent } from '../NoDataComponent';

/**
 * Section Component
 * A generic component that handles displaying paginated or non-paginated lists of items with loading states,
 * error handling, and pagination controls.
 *
 * @component
 * @template T - The type of data items being displayed
 * @param {Object} props - Component props
 * @param {boolean} props.paginated - Whether the data is paginated
 * @param {'manual' | 'infinite'} props.paginationType - Type of pagination to use
 * @param {Error} props.error - Error object if request failed
 * @param {boolean} props.hasMore - Whether there are more items to load
 * @param {boolean} props.isLoading - Whether initial data is loading
 * @param {boolean} props.isLoadingMore - Whether next page is loading
 * @param {number} props.limit - Number of items per page
 * @param {() => void} props.loadMore - Function to load next page
 * @param {() => void} props.refetch - Function to retry failed request
 * @param {Object} props.componenents - Component renderers
 * @param {(data: T, index: number) => JSX.Element} props.componenents.component - Renders each data item
 * @param {(index: number) => JSX.Element} props.componenents.skeleton - Renders loading placeholder
 * @param {T[]} props.data - Array of data items to display
 * @param {number} props.totalCount - Total number of items available
 * @param {string} props.className - Additional CSS class for content container
 * @returns {JSX.Element} The rendered section component
 *
 * @example
 * type User = {
 *   id: number;
 *   name: string;
 * };
 *
 * <Section
 *   paginated={true}
 *   paginationType="manual"
 *   error={null}
 *   hasMore={true}
 *   isLoading={false}
 *   isLoadingMore={false}
 *   limit={10}
 *   loadMore={() => fetchNextPage()}
 *   refetch={() => refetchData()}
 *   componenents={{
 *     component: (user, index) => <UserCard key={index} user={user} />,
 *     skeleton: (index) => <UserCardSkeleton key={index} />
 *   }}
 *   data={users}
 *   totalCount={100}
 *   className="user-list"
 * />
 */
export function Section<T>({
  paginated,
  paginationType,
  error,
  hasMore,
  isLoading,
  isLoadingMore,
  limit,
  loadMore,
  refetch,
  componenents: { component, skeleton },
  data,
  totalCount,
  className,
}: SectionProps<T>) {
  // Memoized array of skeleton components for loading state
  const Skeletons: JSX.Element[] = useMemo(
    () => Array.from({ length: limit }).map((_, index) => skeleton(index)),
    [limit, skeleton],
  );

  // Memoized array of rendered data components
  const Components: JSX.Element[] = useMemo(
    () => data?.map((data, index) => component(data, index)),
    [data, component],
  );

  // Show error state if there's an error and refetch function available
  if (error && refetch) {
    return (
      <div className={`${styles.container} ${styles['container--error']}}`}>
        <ErrorCard error={error} refetch={refetch} />
      </div>
    );
  }

  // Show no data component if not loading and no data available
  if (!isLoading && data?.length === 0) {
    return <NoDataComponent isSecondaryStyles isSmall />;
  }

  return (
    <div className={styles.container}>
      <div
        className={`${styles.container__content} ${className ? className : ''}`}
      >
        {Components}
        {((paginated && isLoadingMore) || isLoading) && Skeletons}
      </div>

      {!isLoading && paginationType == 'manual' && loadMore && (
        <PaginationWithProgress
          count={(data?.length as number) ?? 0}
          loading={isLoading as boolean}
          fetchNextPage={loadMore}
          isFetchingNextPage={isLoadingMore as boolean}
          hasNextPage={hasMore as boolean}
          totalCount={totalCount as number}
        />
      )}
    </div>
  );
}
